import { CartUpdateEvent, ThemeEvents } from "@theme/events";
import { formatShopifyMoney, fetchConfig } from "@theme/utilities";

class PopupCartAdd extends HTMLElement {
  connectedCallback() {
    this.cartAddUrl = this.dataset.cartAddUrl || "/cart/add";
    this.cartUrl = this.dataset.cartUrl || "/cart";
    this.maxItems = Number(this.dataset.maxItems) || 6;
    this.onlyOnPdp = this.dataset.onlyOnPdp !== "false";
    this.strategy = this.dataset.strategy || "complementary";
    this.fallbackCollection = this.dataset.fallbackCollection || "";
    this.excludedTags = (this.dataset.excludedTags || "")
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

    // Promo-Mode (mirrors FBT logic)
    this.promoMode = this.dataset.promoMode || "off";
    this.promoActive = this.promoMode === "auto";
    this.promoVendor = this.dataset.promoVendor || "";
    this.promoThreshold = Number(this.dataset.promoThreshold) || 4;
    this.promoStep = Number(this.dataset.promoStep) || 3;
    this.promoMaxFree = Number(this.dataset.promoMaxFree) || 4;
    this.tierTopQty = Number(this.dataset.tierTopQty) || 13;
    this.cartVendorQty = 0;

    this.promoEl = this.querySelector("[data-popup-promo]");
    this.promoFillEl = this.querySelector("[data-popup-promo-fill]");
    this.promoHeadlineEl = this.querySelector("[data-popup-promo-headline]");
    this.promoHintEl = this.querySelector("[data-popup-promo-hint]");
    this.promoEyebrowEl = this.querySelector("[data-popup-promo-eyebrow]");
    this.promoNodes = Array.from(this.querySelectorAll(".popup-promo__node"));
    /** Track previous tier count for celebration triggers */
    this.lastFreeItems = 0;

    // Mini-bar (persistent minimized state) refs
    this.miniBarEl = this.querySelector("[data-mini-bar]");
    this.miniHandleEl = this.querySelector("[data-mini-handle]");
    this.miniCloseEl = this.querySelector("[data-mini-close]");
    this.miniHeadlineEl = this.querySelector("[data-mini-headline]");
    this.miniFillEl = this.querySelector("[data-mini-fill]");
    this.miniTextEl = this.querySelector(".popup-cart-add__mini-text");
    /** State machine: "closed" | "expanded" | "minimized" */
    this.popupState = "closed";

    this.containerEl = this.querySelector(".popup-cart-add__container");
    this.backdropEl = this.querySelector("[data-backdrop]");
    this.closeBtn = this.querySelector("[data-close]");
    this.openCartBtn = this.querySelector("[data-open-cart]");
    this.minimizeBtn = this.querySelector("[data-minimize]");
    this.listEl = this.querySelector("[data-list]");
    this.emptyEl = this.querySelector("[data-empty]");
    this.paginationEl = this.querySelector("[data-pagination]");
    this.prevBtn = this.querySelector("[data-prev]");
    this.nextBtn = this.querySelector("[data-next]");
    this.cardTemplate = this.querySelector("[data-card-template]");

    // Desktop options
    this.desktopMinimize = this.dataset.desktopMinimize !== "false";
    this.desktopMql = window.matchMedia("(min-width: 990px)");
    if (!this.desktopMinimize && this.minimizeBtn) {
      this.minimizeBtn.hidden = true;
    }

    // Adaptive Frequency: ab Add #N → Mini-Toast statt vollem Popup.
    // Counter + Cooldown + Soft-Disable in sessionStorage, damit das
    // Verhalten über PDP-Navigationen erhalten bleibt.
    this.adaptiveEnabled = this.dataset.adaptiveEnabled === "true";
    this.adaptiveThreshold = Number(this.dataset.adaptiveThreshold) || 3;
    this.adaptiveCooldownMs = Number(this.dataset.adaptiveCooldownMs) || 30 * 60 * 1000;
    this.toastDurationMs = Number(this.dataset.toastDurationMs) || 4000;

    this.toastEl = this.querySelector("[data-toast]");
    this.toastBodyEl = this.querySelector("[data-toast-body]");
    this.toastHeadlineEl = this.querySelector("[data-toast-headline]");
    this.toastCartEl = this.querySelector("[data-toast-cart]");
    this.toastCloseBtn = this.querySelector("[data-toast-close]");
    this.toastShowRecsBtn = this.querySelector("[data-toast-show-recs]");
    this.hideRecsBtn = this.querySelector("[data-hide-recs]");
    this.toastDismissTimer = null;
    this.lastAddedTitle = "";

    this.products = [];
    this.activeIndex = 0;
    this.lastTriggerProductId = null;
    this.isOpen = false;
    this.isAddingItem = false;

    // Wire close + nav handlers
    this.closeBtn?.addEventListener("click", () => this.close({ openDrawer: false }));
    this.backdropEl?.addEventListener("click", () => this.handleOutsideClick());
    // "Warenkorb öffnen" → premium sequential flow:
    //   - On desktop with active promo: minimize popup to mini-pill, then
    //     open the cart-drawer. Customer keeps tier progress visible while
    //     reviewing the cart, and can re-engage upsells with one click.
    //   - Mobile or non-promo: close popup fully + open drawer (legacy).
    this.openCartBtn?.addEventListener("click", () => this.handleOpenCart());
    this.minimizeBtn?.addEventListener("click", () => this.minimize());
    this.prevBtn?.addEventListener("click", () => this.navigate(-1));
    this.nextBtn?.addEventListener("click", () => this.navigate(1));

    // Adaptive Frequency wirings
    this.toastBodyEl?.addEventListener("click", () => this.handleToastOpenCart());
    this.toastCloseBtn?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.hideToast({ persist: false });
    });
    this.toastShowRecsBtn?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.handleToastShowRecommendations();
    });
    this.hideRecsBtn?.addEventListener("click", () => this.handleHideRecommendations());
    // Pause auto-dismiss on hover (Desktop) — User reads/decides ohne dass Toast verschwindet
    this.toastEl?.addEventListener("pointerenter", () => this.pauseToastDismiss());
    this.toastEl?.addEventListener("pointerleave", () => this.resumeToastDismiss());


    // Apple-style drag-to-dismiss on the bottom-sheet handle (mobile only)
    this.setupDragToDismiss();
    // Mini-bar gestures: tap to expand, drag-up to expand, X to dismiss
    this.setupMiniBarGestures();
    // Persist minimized state when user clicks a recommended product link
    // so the mini-bar follows them onto the next PDP.
    this.setupCardLinkPersistence();
    // Restore minimized state from sessionStorage if applicable
    this.tryRestorePersistedState();

    // Listen for cart updates in CAPTURE phase so we run BEFORE Pebble's
    // cart-drawer-component listener (which is bubble-phase by default).
    // We synchronously set shouldSuppressDrawer = true before any await,
    // so when the drawer's listener fires next, our monkey-patched showDialog
    // sees the flag and early-returns.
    this.cartUpdateHandler = this.handleCartUpdate.bind(this);
    const eventName = ThemeEvents?.cartUpdate || "cart:update";
    document.addEventListener(eventName, this.cartUpdateHandler, { capture: true });

    // Patch the cart-drawer's showDialog as soon as the element is defined.
    if (window.customElements?.whenDefined) {
      Promise.race([
        customElements.whenDefined("cart-drawer-component"),
        customElements.whenDefined("cart-drawer"),
      ])
        .then(() => this.patchCartDrawer())
        .catch(() => this.patchCartDrawer());
    } else {
      this.patchCartDrawer();
    }

    // ESC closes
    this.escHandler = (e) => {
      if (e.key === "Escape" && this.isOpen) this.close();
    };
    document.addEventListener("keydown", this.escHandler);
  }

  disconnectedCallback() {
    const eventName = ThemeEvents?.cartUpdate || "cart:update";
    document.removeEventListener(eventName, this.cartUpdateHandler);
    document.removeEventListener("keydown", this.escHandler);
  }

  /**
   * Click outside the popup container (backdrop). On desktop with active
   * promo we minimize to the mini-pill instead of fully closing — preserves
   * tier progress without forcing the customer to commit to "close". Mobile
   * + non-promo still close as before.
   */
  handleOutsideClick() {
    const desktop = this.desktopMql?.matches;
    if (desktop && this.desktopMinimize && this.shouldShowPromo && this.miniBarEl) {
      const free = this.computeFreeItems(this.cartVendorQty);
      if (free < this.promoMaxFree) {
        this.minimize();
        return;
      }
    }
    this.close({ openDrawer: false });
  }

  /**
   * "Warenkorb öffnen" CTA: sequential cart-review flow.
   *
   * Desktop + active promo + mini-bar available:
   *   Popup minimizes to the mini-pill (tier progress stays visible bottom-
   *   left), drawer opens on the right. The customer reviews the cart
   *   without losing the upsell context.
   *
   * Mobile or no promo:
   *   Popup closes fully and the drawer opens — legacy behavior, no pill
   *   to demote to.
   */
  handleOpenCart() {
    // Cart-Engagement = logischer Trennpunkt: Counter resetten, damit der
    // nächste Add wieder als #1 zählt. sessionDisabled bleibt unangetastet
    // (User-Wahl "ausblenden" respektieren).
    this.resetAdaptiveCount();

    const desktop = this.desktopMql?.matches;
    const canMinimize =
      desktop &&
      this.desktopMinimize &&
      this.shouldShowPromo &&
      this.miniBarEl &&
      this.computeFreeItems(this.cartVendorQty) < this.promoMaxFree;

    if (canMinimize) {
      // Allow drawer to open while we're transitioning — clear suppression
      // immediately so the showDialog patch lets it through.
      this.shouldSuppressDrawer = false;
      this.setState("minimized");
      // Open drawer on the next frame so the popup → pill animation has
      // a moment to start before the drawer slide-in begins. Feels less
      // chaotic than firing both transitions on the exact same tick.
      requestAnimationFrame(() => this.openCartDrawerNow());
      return;
    }

    this.close({ openDrawer: true });
  }

  /**
   * Patch every cart-drawer instance's showDialog() so it early-returns while
   * our popup is showing OR scheduled to show. Re-tries until found because
   * the cart-drawer custom element may be defined AFTER our connectedCallback.
   */
  patchCartDrawer() {
    const tryPatch = () => {
      const drawers = document.querySelectorAll("cart-drawer-component, cart-drawer");
      let patchedAny = false;
      drawers.forEach((drawer) => {
        if (drawer.__mammPopupPatched) {
          patchedAny = true;
          return;
        }
        const originalShow = drawer.showDialog;
        if (typeof originalShow !== "function") return;
        drawer.showDialog = (...args) => {
          // shouldSuppressDrawer is the single source of truth.
          // It's set true synchronously in handleCartUpdate when we want
          // the popup to take over solo, and false when desktop parallel
          // drawer-open is enabled. Don't gate on isOpen — that would
          // block the drawer once setState flips isOpen=true on expand.
          if (this.shouldSuppressDrawer) return;
          return originalShow.apply(drawer, args);
        };
        drawer.__mammPopupPatched = true;
        patchedAny = true;
      });
      return patchedAny;
    };

    if (tryPatch()) return;

    const interval = setInterval(() => {
      if (tryPatch()) clearInterval(interval);
    }, 100);
    setTimeout(() => clearInterval(interval), 8000);
  }

  async handleCartUpdate(event) {
    if (this.isAddingItem) return;

    const data = event?.detail?.data || {};
    const source = data.source || "";

    // Always refresh mini-bar tier if minimized — even non-add events
    // (drawer remove, qty change) should update tier progress live.
    if (this.popupState === "minimized" && this.shouldShowPromo) {
      this.refreshCartVendorQty();
    }

    // The rest of this handler triggers the EXPANDED popup. Skip it for
    // non-add events so we don't accidentally re-open from drawer interactions.
    if (data.isIncremental !== true) return;

    if (source === "frequently-bought-together" || source === "cart-add-popup") return;
    if (source === "cart-items-component") return;

    if (this.onlyOnPdp) {
      const tpl = window.FoxTheme?.template?.name;
      if (tpl !== "product") return;
    }

    const productId = this.detectCurrentProductId();
    if (!productId) return;

    // Adaptive Frequency: ab Add #threshold zeigen wir nur einen Mini-Toast
    // (statt des vollen Popups). Counter, Cooldown und Soft-Disable in
    // sessionStorage. Entscheidung MUSS vor dem `shouldSuppressDrawer = true`
    // fallen, damit bei Toast-Pfad der Cart-Drawer normal aufgehen darf
    // wenn das Theme so konfiguriert ist.
    const decision = this.evaluateAdaptiveDecision();

    if (decision === "toast") {
      // Toast-Pfad: drawer NICHT suppressen — User soll Cart-Drawer
      // unbeeinträchtigt nutzen können (öffnet er via Toast-Klick).
      this.shouldSuppressDrawer = false;
      this.lastTriggerProductId = productId;
      this.showToast(productId);
      return;
    }

    // CRITICAL: set the suppression flag SYNCHRONOUSLY before any await.
    // Pebble's cart-drawer listener (bubble phase) runs after this capture-phase
    // handler returns control; the patched showDialog sees the flag and early-exits.
    //
    // Sequential flow: Add → Popup only. Drawer never auto-opens. The
    // customer explicitly clicks "Warenkorb öffnen" → popup minimizes →
    // drawer opens. This avoids cognitive overload (two modals competing
    // for attention) and the Pebble-drawer top-layer hit-testing fight.
    this.shouldSuppressDrawer = true;
    this.lastTriggerProductId = productId;

    // Promo only fires when the just-added product matches the promo vendor.
    // If the customer adds a Tuga Chemie cleaner on a Tuga PDP, showing the
    // AntiDirt Mix & Match hero would be confusing — they'd wonder why their
    // current purchase isn't counting toward the tier. So we gate the promo
    // hero on a PDP-vendor match.
    const currentVendor = this.detectCurrentProductVendor();
    this.shouldShowPromo =
      this.promoActive &&
      !!currentVendor &&
      !!this.promoVendor &&
      currentVendor.toLowerCase() === this.promoVendor.toLowerCase();

    // Desktop minimize button is only useful when there's promo progress to
    // display in the mini-pill. Without promo, "minimize" would degrade to a
    // close — so we hide the button entirely for non-promo PDPs.
    this.dataset.showMinimize =
      this.desktopMinimize && this.shouldShowPromo ? "true" : "false";

    // Skeleton-first UX: open the popup IMMEDIATELY with shimmer placeholders,
    // then load real recommendations and swap them in. Improves perceived
    // performance — the user sees instant feedback instead of a 200-500ms
    // blank delay before the popup appears.
    this.renderSkeletons(4);
    this.open();

    // Fetch cart vendor qty in parallel ONLY if we'll actually show the promo
    if (this.shouldShowPromo) {
      this.refreshCartVendorQty();
    } else if (this.promoEl) {
      // Hide promo hero for non-vendor adds — popup behaves as standard cross-sell
      this.promoEl.hidden = true;
    }

    try {
      await this.loadRecommendations(productId);
      this.render();
    } catch (err) {
      console.warn("[CartAddPopup] failed to load recommendations", err);
      this.shouldSuppressDrawer = false;
    }
  }

  async refreshCartVendorQty() {
    try {
      const res = await fetch(`${this.cartUrl}.js`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return;
      const cart = await res.json();
      this.cartVendorQty = cart.items
        .filter((i) => i.vendor === this.promoVendor)
        .reduce((s, i) => s + i.quantity, 0);
      this.updatePromoTrack();
      this.updateMiniBar();
    } catch {
      // best-effort
    }
  }

  computeFreeItems(qty) {
    if (!this.promoActive || qty < this.promoThreshold) return 0;
    const free = Math.floor((qty - this.promoThreshold) / this.promoStep) + 1;
    return Math.min(free, this.promoMaxFree);
  }

  /**
   * Render the promo hero. Progress bar represents progress to the NEXT tier
   * only (not the top tier) — so being 3/4 toward tier 1 shows as 75%, which
   * matches user expectation. After hitting tier 1 (qty=4), the bar resets
   * and starts climbing toward tier 2 (qty=7), etc.
   */
  updatePromoTrack() {
    if (!this.promoActive || !this.shouldShowPromo || !this.promoEl) return;

    const qty = this.cartVendorQty;
    const free = this.computeFreeItems(qty);

    this.promoEl.hidden = false;

    let pct, headline, hint, state;

    // Visual scale rebases on each tier-up so the bar always spans
    // 0 → next-tier-qty (or 0 → tierTopQty when max is reached). This keeps
    // the customer focused on the *immediate* goal — reached tiers and the
    // next tier are visible as milestones, future-future tiers are hidden.
    const isMax = free >= this.promoMaxFree;
    const nextTierQty = isMax
      ? this.tierTopQty
      : this.promoThreshold + this.promoStep * free;
    const visualMaxQty = nextTierQty;
    const absolutePct = Math.max(0, Math.min(100, (qty / visualMaxQty) * 100));

    if (qty < this.promoThreshold) {
      // STATE 1: below first tier
      const need = this.promoThreshold - qty;
      pct = absolutePct;
      const productNoun = need === 1 ? "Produkt" : "Produkte";
      const articleNoun = need === 1 ? "Artikel" : "Artikel"; // both forms identical
      headline = `Noch ${need} ${this.promoVendor} ${productNoun} bis zur ersten Stufe`;
      hint = `Lege noch ${need} ${articleNoun} von ${this.promoVendor} in den Warenkorb, erhalte das günstigste davon kostenlos.`;
      state = "below";
    } else if (isMax) {
      // STATE 3: max tier reached
      pct = 100;
      headline = "Maximaler Stufen-Rabatt erreicht";
      hint = `Du erhältst ${free} Artikel kostenlos.`;
      state = "max";
    } else {
      // STATE 2: between tiers
      const need = nextTierQty - qty;
      pct = absolutePct;
      const productNoun = need === 1 ? "Produkt" : "Produkte";
      const currentFreeLabel = free === 1 ? "1 Artikel" : `${free} Artikel`;
      const nextFreeLabel = free + 1 === 1 ? "1 Artikel" : `${free + 1} Artikel`;
      headline = `Noch ${need} ${productNoun} bis zur nächsten Stufe`;
      // Render hint as DOM so the achievement-so-far phrase stands out in bold.
      // Direct DOM manipulation avoids any innerHTML-XSS risk from vendor strings.
      if (this.promoHintEl) {
        this.promoHintEl.innerHTML = "";
        const strong = document.createElement("strong");
        strong.textContent = `Aktuell ${currentFreeLabel} gratis gesichert.`;
        this.promoHintEl.appendChild(strong);
        this.promoHintEl.appendChild(
          document.createTextNode(` Mit ${need} mehr erhältst du ${nextFreeLabel} gratis.`)
        );
      }
      hint = null; // marker: hint already rendered above
      state = "active";
    }

    // The rightmost visible tier marker sits at TIER_GOAL_POS_PCT (not at
    // 100%) so its centered "X Artikel gratis" label never gets clipped by
    // the popup's right edge. Bar fill is scaled to the same anchor for
    // visual consistency.
    const TIER_GOAL_POS_PCT = 88;
    pct = isMax ? TIER_GOAL_POS_PCT : Math.min(pct, 100) * (TIER_GOAL_POS_PCT / 100);

    if (this.promoFillEl) {
      this.promoFillEl.style.setProperty("--w", `${Math.max(0, Math.min(100, pct)).toFixed(2)}%`);
    }
    if (this.promoHeadlineEl) this.promoHeadlineEl.textContent = headline;
    if (this.promoHintEl && hint !== null) this.promoHintEl.textContent = hint;
    this.promoEl.dataset.state = state;

    // Tier-node visibility + position:
    //   - All reached tiers stay visible (filled with checkmark)
    //   - The single "next" tier is visible as the immediate goal
    //   - All future-future tiers are hidden — focus on the next milestone
    //   - Positions rebase to visualMaxQty so reached tiers spread evenly
    //     along the bar that always ends at the next-goal anchor.
    if (this.promoNodes && this.promoNodes.length) {
      const nextVisibleIndex = isMax ? this.promoMaxFree : free + 1;
      this.promoNodes.forEach((node) => {
        const tierIndex = Number(node.dataset.tierIndex) || 0;
        const tierQty = Number(node.dataset.tierQty) || 0;
        const reached = qty >= tierQty;
        const visible = tierIndex <= nextVisibleIndex;
        node.dataset.tierReached = reached ? "true" : "false";
        node.dataset.tierVisible = visible ? "true" : "false";
        if (visible) {
          const posPct = (tierQty / visualMaxQty) * TIER_GOAL_POS_PCT;
          node.style.setProperty("--pos", `${posPct.toFixed(2)}%`);
        }
      });
    }

    // Celebration pulse on tier-up
    if (free > this.lastFreeItems && this.lastFreeItems !== undefined) {
      this.promoEl.dataset.celebrating = "true";
      setTimeout(() => {
        if (this.promoEl) delete this.promoEl.dataset.celebrating;
      }, 1500);
    }
    this.lastFreeItems = free;
  }

  renderSkeletons(count = 4) {
    if (!this.listEl) return;
    this.listEl.innerHTML = "";
    if (this.emptyEl) this.emptyEl.hidden = true;
    if (this.paginationEl) this.paginationEl.innerHTML = "";
    if (this.prevBtn) this.prevBtn.toggleAttribute("data-disabled", true);
    if (this.nextBtn) this.nextBtn.toggleAttribute("data-disabled", true);

    for (let i = 0; i < count; i++) {
      const li = document.createElement("li");
      li.className = "popup-cart-add__card popup-cart-add__card--skeleton";
      li.style.setProperty("--idx", i);
      li.innerHTML = `
        <div class="popup-cart-add__card-link" aria-hidden="true"></div>
        <div class="popup-cart-add__card-content">
          <span class="popup-cart-add__card-title" data-skeleton-block style="height:1.4rem;width:75%"></span>
          <span class="popup-cart-add__card-title" data-skeleton-block style="height:1.4rem;width:55%;margin-top:0.4rem"></span>
          <span class="popup-cart-add__card-price-actual" data-skeleton-block style="height:1.4rem;width:35%;margin-top:0.8rem"></span>
        </div>
        <div class="popup-cart-add__card-add" aria-hidden="true"></div>
      `;
      this.listEl.appendChild(li);
    }
  }

  detectCurrentProductId() {
    // Read from <main> data attr if present, otherwise from any product-form
    const main = document.querySelector("[data-product-id]");
    if (main?.dataset.productId) return String(main.dataset.productId);
    const form = document.querySelector('product-form[data-product-id], form[data-product-id]');
    if (form?.dataset.productId) return String(form.dataset.productId);
    return null;
  }

  /**
   * Read the current PDP product's vendor (added via data-product-vendor on
   * product-form-component). Used to decide whether to display the promo
   * hero — we only show it when the just-added product matches the promo
   * vendor, otherwise the promo would feel like noise to the customer.
   */
  detectCurrentProductVendor() {
    const form = document.querySelector("[data-product-vendor]");
    if (form?.dataset.productVendor) return form.dataset.productVendor;
    if (window.ShopifyAnalytics?.meta?.product?.vendor) {
      return window.ShopifyAnalytics.meta.product.vendor;
    }
    return null;
  }

  /**
   * Strategy-driven loader with verbose logging so you can see exactly
   * what each strategy returns. Open DevTools console after add-to-cart.
   */
  async loadRecommendations(productId) {
    const cart = await fetch(`${this.cartUrl}.js`, {
      headers: { Accept: "application/json" },
    })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .catch(() => ({ items: [] }));

    const cartVariantIds = new Set((cart.items || []).map((i) => String(i.variant_id)));

    const matchesFilter = (p) => {
      if (!p) return false;
      if (String(p.id) === String(productId)) return false;
      const variant = p.variants?.[0];
      if (!variant) return false;
      if (variant.available === false) return false;
      if (cartVariantIds.has(String(variant.id))) return false;
      // Exclude products carrying any of the merchant-configured blacklist tags
      if (this.excludedTags.length > 0 && Array.isArray(p.tags)) {
        const productTags = p.tags.map((t) => String(t).toLowerCase());
        if (this.excludedTags.some((t) => productTags.includes(t))) return false;
      }
      return true;
    };

    console.group(`[CartAddPopup] Loading recommendations`);
    console.info("Strategy:", this.strategy);
    console.info("Fallback collection:", this.fallbackCollection || "(none configured)");
    console.info("Current product ID:", productId);
    console.info("Max items:", this.maxItems);

    let filtered = [];
    const apiResults = {};

    // STEP 0: read static metafield products from popup section (server-side
    // rendered on PDP load via product.metafields.custom.frequently_bought_together).
    // This is the highest-priority source — the section render API endpoint
    // doesn't reliably expose product.metafields, so we use the popup's own
    // server-side render context which DOES have product available.
    const staticMfProducts = this.readStaticMetafieldProducts();
    if (staticMfProducts.length > 0) {
      console.info(
        `Statisch (Metafield) → ${staticMfProducts.length} produkte:`,
        staticMfProducts.map((p) => p.title)
      );
      for (const p of staticMfProducts.filter(matchesFilter)) {
        if (filtered.length >= this.maxItems) break;
        filtered.push(p);
      }
    }

    // Step 1: API recommendations (skipped when strategy=collection)
    // Note: deduplication via seen-set prevents duplicates with metafield products.
    if (this.strategy !== "collection") {
      const intents =
        this.strategy === "auto" ? ["complementary", "related"] : [this.strategy];
      for (const intent of intents) {
        if (filtered.length >= this.maxItems) break;
        const apiProducts = await this.fetchRecommendations(productId, intent);
        apiResults[intent] = apiProducts.map((p) => p.title);
        console.info(
          `intent=${intent} → ${apiProducts.length} produkte:`,
          apiProducts.map((p) => p.title)
        );
        const seen = new Set(filtered.map((p) => String(p.id)));
        for (const p of apiProducts.filter(matchesFilter)) {
          if (filtered.length >= this.maxItems) break;
          if (seen.has(String(p.id))) continue;
          filtered.push(p);
          seen.add(String(p.id));
        }
      }
      console.info(`Nach API-Filter (verfuegbar + nicht im Cart): ${filtered.length} produkte`);
    }

    // Step 2: collection fallback (when collection-only OR sparse API results)
    const useCollection =
      (this.strategy === "collection" || filtered.length < this.maxItems) &&
      this.fallbackCollection;

    if (useCollection) {
      try {
        const collProducts = await this.fetchCollectionProducts(this.fallbackCollection);
        console.info(
          `Collection "${this.fallbackCollection}" → ${collProducts.length} produkte (vor shuffle):`,
          collProducts.map((p) => p.title)
        );
        // Shuffle so the same products don't always appear in the same order
        const shuffled = this.shuffle(collProducts);
        const seen = new Set(filtered.map((p) => String(p.id)));
        let added = 0;
        for (const p of shuffled.filter(matchesFilter)) {
          if (filtered.length >= this.maxItems) break;
          if (seen.has(String(p.id))) continue;
          filtered.push(p);
          seen.add(String(p.id));
          added++;
        }
        console.info(`Collection-Fallback fuegte ${added} produkte hinzu`);
      } catch (err) {
        console.warn("[CartAddPopup] collection fallback failed", err);
      }
    } else if (this.strategy !== "collection" && filtered.length < this.maxItems) {
      console.warn(
        "API liefert weniger als max_items. Setze eine Fallback-Collection in den Section-Settings, um die Liste aufzufuellen."
      );
    }

    // Shuffle ONLY non-metafield products. Metafield products are merchant-
    // curated priorities — their order is intentional and must be preserved.
    // API/collection products get shuffled for variety on repeat opens.
    const mfTagged = filtered.filter((p) => p.source === "metafield");
    const nonMf = this.shuffle(filtered.filter((p) => p.source !== "metafield"));
    filtered = [...mfTagged, ...nonMf];

    // Vendor-priority is only relevant when the promo will be SHOWN — i.e.,
    // when we're on a vendor-matching PDP. On a non-vendor PDP (e.g. Tuga),
    // sorting AntiDirt to the front would actively hurt the cross-sell intent
    // (we'd push unrelated brand products instead of products complementing
    // what the customer just bought).
    if (this.shouldShowPromo && this.promoVendor) {
      const vendorMatches = filtered.filter((p) => p.vendor === this.promoVendor);
      const others = filtered.filter((p) => p.vendor !== this.promoVendor);
      filtered = [...vendorMatches, ...others];
      console.info(
        `Promo aktiv: ${vendorMatches.length} ${this.promoVendor}-Produkte vorne, ${others.length} weitere danach`
      );
    }

    console.info(
      `FINAL (${filtered.length} produkte):`,
      filtered.map((p) => p.title)
    );
    console.groupEnd();

    this.products = filtered;
    this.activeIndex = 0;
  }

  /**
   * Read products from the server-rendered metafield script tag inside the
   * popup section. Empty array if not on a PDP or metafield is unset.
   */
  readStaticMetafieldProducts() {
    const node = this.querySelector("[data-popup-mf-products]");
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.textContent || "{}");
      return Array.isArray(parsed.products) ? parsed.products : [];
    } catch (err) {
      console.warn("[CartAddPopup] could not parse static metafield products", err);
      return [];
    }
  }

  shuffle(arr) {
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }

  /**
   * Fetch via the Section Render API mirror of Pebble's product-recommendations.
   * This uses Shopify's server-side `recommendations.products` Liquid object,
   * which has built-in fallbacks (collection.all etc.) that the JSON-only
   * endpoint /recommendations/products.json lacks. Result: many more relevant
   * products on stores with limited bought-together data.
   */
  async fetchRecommendations(productId, intent) {
    const url = `/recommendations/products?section_id=cart-add-recommendations&product_id=${encodeURIComponent(
      productId
    )}&limit=${this.maxItems}&intent=${intent}`;
    try {
      const res = await fetch(url, { headers: { Accept: "text/html" } });
      if (!res.ok) {
        console.warn(`[CartAddPopup] /recommendations HTTP ${res.status} for intent=${intent}`);
        return [];
      }
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const dataNode = doc.querySelector("[data-cart-add-recs]");
      if (!dataNode) {
        console.warn("[CartAddPopup] no data tag in section response");
        return [];
      }
      const parsed = JSON.parse(dataNode.textContent || "{}");
      return Array.isArray(parsed.products) ? parsed.products : [];
    } catch (err) {
      console.warn(`[CartAddPopup] /recommendations fetch failed for intent=${intent}`, err);
      return [];
    }
  }

  async fetchCollectionProducts(handle) {
    if (!handle) return [];
    const url = `/collections/${encodeURIComponent(handle)}/products.json?limit=${this.maxItems * 3}`;
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) {
      console.warn(`[CartAddPopup] /collections/${handle}/products.json HTTP ${res.status}`);
      return [];
    }
    const data = await res.json();
    return Array.isArray(data.products) ? data.products : [];
  }

  parseCents(value) {
    if (value == null) return 0;
    if (typeof value === "number") return Math.round(value);
    const str = String(value);
    if (str.includes(".")) return Math.round(parseFloat(str) * 100);
    const num = Number(str);
    return Number.isFinite(num) ? Math.round(num) : 0;
  }

  render() {
    if (!this.listEl || !this.cardTemplate) return;
    this.listEl.innerHTML = "";

    if (this.products.length === 0) {
      this.emptyEl?.removeAttribute("hidden");
      this.paginationEl.innerHTML = "";
      this.updateNavState();
      return;
    }
    this.emptyEl?.setAttribute("hidden", "");

    this.products.forEach((p, idx) => {
      const card = this.buildCard(p, idx);
      if (card) this.listEl.appendChild(card);
    });

    this.renderPagination();
    this.scrollToActive(false);
    this.updateNavState();
  }

  buildCard(p, idx) {
    const fragment = this.cardTemplate.content.cloneNode(true);
    const li = fragment.querySelector("[data-card]");
    if (!li) return null;
    li.dataset.productId = p.id;
    li.dataset.index = idx;
    // CSS uses --idx for stagger-entrance animation delay
    li.style.setProperty("--idx", idx);

    const variant = p.variants?.[0];
    if (!variant) return null;
    const priceCents = this.parseCents(variant.price ?? p.price);
    const compareCents = this.parseCents(variant.compare_at_price ?? p.compare_at_price);

    // Tag the card so CSS can apply sale-price color when on sale
    if (compareCents > priceCents) {
      li.dataset.onSale = "true";
    }

    const link = fragment.querySelector("[data-card-link]");
    const titleLink = fragment.querySelector("[data-card-title-link]");
    const img = fragment.querySelector("[data-card-image]");
    const badge = fragment.querySelector("[data-card-badge]");
    const variantEl = fragment.querySelector("[data-card-variant]");
    const priceEl = fragment.querySelector("[data-card-price]");
    const compareEl = fragment.querySelector("[data-card-compare]");
    const addBtn = fragment.querySelector("[data-card-add]");
    const promoTag = fragment.querySelector("[data-card-promo-tag]");

    // "Zählt zur Aktion"-Badge only when the popup is actually showing the
    // promo (i.e., user came from a vendor-matching PDP) AND this card itself
    // matches the vendor. Otherwise it's misleading.
    if (
      this.shouldShowPromo &&
      this.promoVendor &&
      p.vendor === this.promoVendor &&
      promoTag
    ) {
      promoTag.hidden = false;
      li.dataset.promoMatch = "true";
    }

    const safeUrl = this.safeProductUrl(p.url, p.handle);
    if (link) link.href = safeUrl;
    if (titleLink) {
      titleLink.href = safeUrl;
      titleLink.textContent = p.title || "";
    }

    // Image source can come from:
    // - section render: `featured_image` (URL string from image_url filter)
    // - /collections/X/products.json: `images` (array of URLs) or `featured_image` (object)
    // - /products.json: same as collections endpoint
    const imgSrc = this.extractImageUrl(p);
    if (img && this.isSafeImageUrl(imgSrc)) {
      img.src = imgSrc;
      img.alt = p.title || "";
    } else if (img) {
      img.remove();
    }

    if (variant.title && variant.title !== "Default Title" && variantEl) {
      variantEl.textContent = variant.title;
      variantEl.hidden = false;
    }

    if (priceEl) priceEl.textContent = formatShopifyMoney(priceCents);
    if (compareEl && compareCents > priceCents) {
      compareEl.textContent = formatShopifyMoney(compareCents);
      compareEl.hidden = false;
    }

    if (badge && compareCents > priceCents) {
      const pct = Math.round(((compareCents - priceCents) / compareCents) * 100);
      badge.textContent = `Aktion · -${pct}%`;
      badge.hidden = false;
    }

    if (addBtn) {
      addBtn.dataset.variantId = variant.id;
      addBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.handleCardAdd(li, variant.id);
      });
    }

    return fragment;
  }

  renderPagination() {
    if (!this.paginationEl) return;
    this.paginationEl.innerHTML = "";
    this.products.forEach((_, idx) => {
      const dot = document.createElement("li");
      dot.className = "popup-cart-add__dot";
      if (idx === this.activeIndex) dot.dataset.active = "true";
      dot.addEventListener("click", () => {
        this.activeIndex = idx;
        this.scrollToActive(true);
        this.updatePaginationActive();
        this.updateNavState();
      });
      this.paginationEl.appendChild(dot);
    });
  }

  updatePaginationActive() {
    if (!this.paginationEl) return;
    Array.from(this.paginationEl.children).forEach((el, idx) => {
      if (idx === this.activeIndex) el.dataset.active = "true";
      else delete el.dataset.active;
    });
  }

  updateNavState() {
    const hasItems = this.products.length > 1;
    if (this.prevBtn) {
      this.prevBtn.toggleAttribute("data-disabled", !hasItems || this.activeIndex === 0);
    }
    if (this.nextBtn) {
      this.nextBtn.toggleAttribute(
        "data-disabled",
        !hasItems || this.activeIndex >= this.products.length - 1
      );
    }
  }

  navigate(delta) {
    const next = this.activeIndex + delta;
    if (next < 0 || next >= this.products.length) return;
    this.activeIndex = next;
    this.scrollToActive(true);
    this.updatePaginationActive();
    this.updateNavState();
  }

  scrollToActive(smooth) {
    const card = this.listEl?.children?.[this.activeIndex];
    if (!card) return;
    card.scrollIntoView({
      behavior: smooth ? "smooth" : "auto",
      block: "nearest",
      inline: "start",
    });
  }

  async handleCardAdd(li, variantId) {
    if (!variantId || this.isAddingItem) return;
    this.isAddingItem = true;
    li.dataset.adding = "true";

    try {
      const addUrl = this.cartAddUrl.endsWith(".js") ? this.cartAddUrl : `${this.cartAddUrl}.js`;
      const res = await fetch(
        addUrl,
        fetchConfig("json", {
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        })
      );
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || "Add failed");

      // Optimistic UI: animate card out and remove from list
      await this.animateRemoveCard(li);
      this.removeProduct(li);

      // Refresh cart-aware components silently (cart count badge, etc.)
      this.dispatchSilentCartUpdate();

      // Refresh tier-track with new cart state (vendor qty may have increased)
      if (this.promoActive) {
        this.refreshCartVendorQty();
      }

      if (this.products.length === 0) {
        this.close({ openDrawer: false });
      }
    } catch (err) {
      console.warn("[CartAddPopup] add failed", err);
      delete li.dataset.adding;
    } finally {
      this.isAddingItem = false;
    }
  }

  animateRemoveCard(li) {
    return new Promise((resolve) => {
      li.addEventListener("transitionend", resolve, { once: true });
      li.dataset.removing = "true";
      // Safety timeout if transitionend doesn't fire
      setTimeout(resolve, 500);
    });
  }

  removeProduct(li) {
    const idx = Number(li.dataset.index);
    li.remove();
    this.products.splice(idx, 1);
    // Re-index remaining cards
    Array.from(this.listEl.children).forEach((el, i) => {
      el.dataset.index = i;
    });
    if (this.activeIndex >= this.products.length) {
      this.activeIndex = Math.max(0, this.products.length - 1);
    }
    this.renderPagination();
    this.scrollToActive(true);
    this.updateNavState();
  }

  async dispatchSilentCartUpdate() {
    // Update cart count badge etc. without re-triggering our own popup.
    try {
      const res = await fetch(`${this.cartUrl}.js`, {
        headers: { Accept: "application/json" },
      });
      const cart = await res.json();
      this.dispatchEvent(
        new CartUpdateEvent(cart, "cart-add-popup", {
          source: "cart-add-popup",
          itemCount: cart.item_count || 0,
        })
      );
    } catch {
      // best-effort
    }
  }

  /**
   * Single source-of-truth state machine: "closed" | "expanded" | "minimized".
   * Persists across page navigations via sessionStorage so the mini-bar
   * survives PDP browsing (vendor-matching PDPs only — see tryRestorePersistedState).
   */
  setState(newState, opts = {}) {
    const { persist = true, fromMini = false } = opts;
    const prevState = this.popupState;
    this.popupState = newState;
    this.isOpen = newState === "expanded";

    if (newState === "closed") {
      delete this.dataset.state;
      delete this.dataset.fromMini;
      if (this.containerEl) this.containerEl.hidden = true;
      if (this.miniBarEl) {
        delete this.miniBarEl.dataset.visible;
        setTimeout(() => {
          if (this.miniBarEl && this.popupState === "closed") this.miniBarEl.hidden = true;
        }, 320);
      }
      document.documentElement.style.overflow = "";
      this.shouldSuppressDrawer = false;
      // Hide entire element after exit animation completes
      setTimeout(() => {
        if (this.popupState === "closed") this.setAttribute("hidden", "");
      }, 320);
    } else if (newState === "expanded") {
      this.removeAttribute("hidden");
      this.dataset.state = "open";
      // Mark whether this expand came from a fresh cart-add (success message
      // shown) or from the mini-bar (no recent add — hide success message).
      if (fromMini) {
        this.dataset.fromMini = "true";
      } else {
        delete this.dataset.fromMini;
      }
      if (this.containerEl) this.containerEl.hidden = false;
      if (this.miniBarEl) {
        delete this.miniBarEl.dataset.visible;
        setTimeout(() => {
          if (this.miniBarEl && this.popupState !== "minimized") this.miniBarEl.hidden = true;
        }, 320);
      }
      document.documentElement.style.overflow = "hidden";
      this.shouldSuppressDrawer = true;
      requestAnimationFrame(() => {
        this.closeBtn?.focus();
      });
    } else if (newState === "minimized") {
      this.removeAttribute("hidden");
      this.dataset.state = "minimized";
      delete this.dataset.fromMini;
      if (this.containerEl) this.containerEl.hidden = true;
      if (this.miniBarEl) {
        this.miniBarEl.hidden = false;
        requestAnimationFrame(() => {
          if (this.miniBarEl) this.miniBarEl.dataset.visible = "true";
        });
      }
      document.documentElement.style.overflow = "";
      this.shouldSuppressDrawer = false;
      this.updateMiniBar();
    }

    if (persist) {
      try {
        if (newState === "minimized") {
          sessionStorage.setItem("mamm-popup-state", "minimized");
        } else if (newState === "closed" && prevState !== "closed") {
          sessionStorage.setItem("mamm-popup-state", "dismissed");
        } else if (newState === "expanded") {
          // Expanded is transient — don't persist (user is actively engaging)
          sessionStorage.removeItem("mamm-popup-state");
        }
      } catch {
        // sessionStorage may be unavailable (private mode, etc.)
      }
    }
  }

  /**
   * Open the popup in expanded state. Legacy wrapper around setState.
   */
  open() {
    if (this.popupState === "expanded") return;
    this.setState("expanded");
  }

  /**
   * Close the popup. By default the user keeps shopping (drawer stays closed).
   * When `openDrawer: true` is passed (via "Warenkorb öffnen" button), the
   * cart-drawer opens after the popup animates out.
   */
  close({ openDrawer = false } = {}) {
    if (this.popupState === "closed") return;
    this.setState("closed");
    if (openDrawer) {
      setTimeout(() => this.openCartDrawerNow(), 320);
    }
  }

  /**
   * Minimize the popup to the bottom-bar. Only valid when promo is active
   * and there's actually progress to display. Falls back to full close if
   * mini-bar isn't available or conditions aren't met.
   */
  minimize() {
    if (!this.miniBarEl || !this.shouldShowPromo) {
      this.close({ openDrawer: false });
      return;
    }
    const free = this.computeFreeItems(this.cartVendorQty);
    if (free >= this.promoMaxFree) {
      // Max tier reached — no progress left to track. Just close.
      this.close({ openDrawer: false });
      return;
    }
    this.setState("minimized");
  }

  /**
   * Manually trigger the cart-drawer (only via the "Warenkorb öffnen" link).
   */
  openCartDrawerNow() {
    const cartType = window.FoxTheme?.settings?.cartType;
    if (cartType !== "drawer") return;
    const drawer = document.querySelector("cart-drawer-component, cart-drawer");
    if (drawer && typeof drawer.showDialog === "function") {
      drawer.showDialog();
    }
  }

  /**
   * Update the mini-bar headline + progress. Identical state logic to
   * updatePromoTrack but condensed for the small persistent-bar UI.
   */
  updateMiniBar() {
    if (!this.miniBarEl || !this.shouldShowPromo) return;

    const qty = this.cartVendorQty;
    const free = this.computeFreeItems(qty);
    // Absolute fill so the mini-bar progress mirrors the popup track and
    // never visually resets when the customer crosses a tier.
    const absolutePct = Math.max(0, Math.min(100, (qty / this.tierTopQty) * 100));
    let pct, headline, state;

    if (qty < this.promoThreshold) {
      pct = absolutePct;
      const need = this.promoThreshold - qty;
      const noun = need === 1 ? "Produkt" : "Produkte";
      headline = `Noch ${need} ${noun} für Gratis-Artikel`;
      state = "below";
    } else if (free >= this.promoMaxFree) {
      pct = 100;
      headline = `Maximaler Rabatt: ${free} Artikel gratis`;
      state = "max";
    } else {
      const nextTierQty = this.promoThreshold + this.promoStep * free;
      const need = nextTierQty - qty;
      pct = absolutePct;
      const noun = need === 1 ? "Produkt" : "Produkte";
      headline = `${free} gratis · Noch ${need} ${noun} bis zur nächsten Stufe`;
      state = "active";
    }

    this.miniBarEl.dataset.state = state;
    if (this.miniHeadlineEl) this.miniHeadlineEl.textContent = headline;
    if (this.miniFillEl) {
      this.miniFillEl.style.setProperty("--w", `${Math.max(0, Math.min(100, pct)).toFixed(2)}%`);
    }
  }

  /**
   * Expand the popup from minimized state. Refetches recommendations based
   * on current PDP context — the cart may have changed since last engagement.
   */
  async expandFromMiniBar() {
    const productId = this.detectCurrentProductId();
    if (!productId) {
      // No PDP context — just open with last-known state, no refetch.
      // fromMini=true so the success header is suppressed.
      this.setState("expanded", { fromMini: true });
      return;
    }

    // Re-evaluate vendor match (in case user navigated to a different PDP)
    const currentVendor = this.detectCurrentProductVendor();
    this.shouldShowPromo =
      this.promoActive &&
      !!currentVendor &&
      !!this.promoVendor &&
      currentVendor.toLowerCase() === this.promoVendor.toLowerCase();

    // Refresh showMinimize button visibility (desktop minimize only makes
    // sense when promo is active, so re-evaluate on each re-expand).
    this.dataset.showMinimize =
      this.desktopMinimize && this.shouldShowPromo ? "true" : "false";

    this.shouldSuppressDrawer = true;
    this.lastTriggerProductId = productId;
    this.renderSkeletons(4);
    // fromMini=true suppresses the "Zum Warenkorb hinzugefügt" success header
    // since the user is just re-engaging, not freshly adding.
    this.setState("expanded", { fromMini: true });

    if (this.shouldShowPromo) {
      this.refreshCartVendorQty();
    } else if (this.promoEl) {
      this.promoEl.hidden = true;
    }

    try {
      await this.loadRecommendations(productId);
      this.render();
    } catch (err) {
      console.warn("[CartAddPopup] expand-from-mini failed", err);
    }
  }

  /**
   * Wire the mini-bar interactions:
   *  - Tap on text or handle → expand
   *  - Drag-up on handle → expand
   *  - Tap X → fully close (sessionStorage marks dismissed)
   */
  setupMiniBarGestures() {
    if (!this.miniBarEl) return;

    let startY = 0;
    let currentY = 0;
    let dragging = false;
    let didDrag = false;

    const onPointerDown = (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      try {
        this.miniHandleEl.setPointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      startY = e.clientY;
      currentY = startY;
      dragging = true;
      didDrag = false;
      this.miniBarEl.dataset.dragging = "true";
    };

    const onPointerMove = (e) => {
      if (!dragging) return;
      currentY = e.clientY;
      const delta = currentY - startY;
      if (Math.abs(delta) > 5) didDrag = true;
      // Allow upward drag (negative delta) — give visual feedback with resistance
      if (delta < 0) {
        this.miniBarEl.style.transform = `translateY(${delta * 0.3}px)`;
      } else {
        // Downward drag at minimized state: small bounce, no dismiss
        this.miniBarEl.style.transform = `translateY(${Math.min(delta * 0.2, 12)}px)`;
      }
    };

    const onPointerUp = (e) => {
      if (!dragging) return;
      dragging = false;
      try {
        this.miniHandleEl.releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      delete this.miniBarEl.dataset.dragging;
      this.miniBarEl.style.transform = "";

      const delta = currentY - startY;
      // Drag-up by > 40px or fast upward flick → expand
      if (delta < -40) {
        this.expandFromMiniBar();
      }
      // Else: tap (no drag) → expand. Tap is detected by NOT didDrag.
    };

    if (this.miniHandleEl) {
      this.miniHandleEl.addEventListener("pointerdown", onPointerDown);
      this.miniHandleEl.addEventListener("pointermove", onPointerMove);
      this.miniHandleEl.addEventListener("pointerup", onPointerUp);
      this.miniHandleEl.addEventListener("pointercancel", onPointerUp);
      // Tap on handle (no drag) → expand
      this.miniHandleEl.addEventListener("click", () => {
        if (!didDrag) this.expandFromMiniBar();
      });
    }

    // Tap on text content → expand (separate from handle for accessibility)
    if (this.miniTextEl) {
      this.miniTextEl.addEventListener("click", () => this.expandFromMiniBar());
    }

    // X button → fully close (sessionStorage marks dismissed)
    if (this.miniCloseEl) {
      this.miniCloseEl.addEventListener("click", () => {
        this.setState("closed");
      });
    }
  }

  /**
   * When the user clicks a recommended product card (image-link or title-link),
   * persist "minimized" state in sessionStorage BEFORE the browser navigates.
   * Result: the mini-bar appears on the next PDP, providing seamless continuity
   * of the promo engagement instead of a hard "popup vanished" experience.
   * Uses event delegation so we don't have to hook every card individually.
   */
  setupCardLinkPersistence() {
    if (!this.listEl) return;
    this.listEl.addEventListener("click", (e) => {
      const link = e.target.closest("[data-card-link], [data-card-title-link]");
      if (!link) return;
      // Only persist when promo is genuinely active and there's progress to show
      if (
        !this.shouldShowPromo ||
        this.cartVendorQty === 0 ||
        this.computeFreeItems(this.cartVendorQty) >= this.promoMaxFree
      ) {
        return;
      }
      try {
        sessionStorage.setItem("mamm-popup-state", "minimized");
      } catch {
        // sessionStorage may be unavailable (private mode); silently skip
      }
    });
  }

  /**
   * Restore mini-bar state from sessionStorage on page load. Only triggers
   * on vendor-matching PDPs to keep the experience consistent — when user
   * navigates to a non-vendor PDP, the bar disappears (state stays in
   * sessionStorage so it returns when they navigate back to vendor PDPs).
   */
  async tryRestorePersistedState() {
    let persisted;
    try {
      persisted = sessionStorage.getItem("mamm-popup-state");
    } catch {
      return;
    }
    if (persisted !== "minimized") return;
    if (!this.miniBarEl) return;
    if (!this.promoActive) return;

    const tpl = window.FoxTheme?.template?.name;
    if (tpl !== "product") return;

    const currentVendor = this.detectCurrentProductVendor();
    if (
      !currentVendor ||
      !this.promoVendor ||
      currentVendor.toLowerCase() !== this.promoVendor.toLowerCase()
    ) {
      return;
    }

    await this.refreshCartVendorQty();
    if (this.cartVendorQty === 0) return;
    if (this.computeFreeItems(this.cartVendorQty) >= this.promoMaxFree) return;

    this.shouldShowPromo = true;
    this.setState("minimized", { persist: false });
  }

  /**
   * Apple-style drag-to-dismiss on the mobile bottom-sheet handle.
   *
   *  - Pointer events with setPointerCapture so the gesture continues even if
   *    finger leaves the handle area mid-drag.
   *  - Sheet follows finger 1:1 downward; backdrop fades proportionally.
   *  - Release threshold: dismiss if dragged > 30% of sheet height OR if
   *    velocity > 0.5 px/ms (fast flick).
   *  - Otherwise CSS transition smoothly snaps the sheet back to position 0.
   *  - touch-action: none in CSS prevents browser scroll interfering.
   */
  setupDragToDismiss() {
    const handle = this.querySelector("[data-popup-handle]");
    if (!handle || !this.containerEl) return;

    let startY = 0;
    let currentY = 0;
    let sheetHeight = 0;
    let startTime = 0;
    let dragging = false;

    const onPointerDown = (e) => {
      if (!this.isOpen) return;
      // Only handle primary button (mouse) or any touch
      if (e.pointerType === "mouse" && e.button !== 0) return;
      try {
        handle.setPointerCapture(e.pointerId);
      } catch {
        // Older browsers — non-fatal
      }
      startY = e.clientY;
      currentY = startY;
      sheetHeight = this.containerEl.offsetHeight || 1;
      startTime = Date.now();
      dragging = true;
      this.dataset.dragging = "true";
    };

    const onPointerMove = (e) => {
      if (!dragging) return;
      currentY = e.clientY;
      const delta = Math.max(0, currentY - startY);
      // Apply slight resistance for upward drags (rubber-band feel) — but
      // since we clamp at 0 above, upward just stays at 0. Down is 1:1.
      this.containerEl.style.transform = `translateY(${delta}px)`;
      if (this.backdropEl) {
        const opacity = Math.max(0.15, 1 - delta / sheetHeight);
        this.backdropEl.style.opacity = String(opacity);
      }
    };

    const onPointerUp = (e) => {
      if (!dragging) return;
      dragging = false;
      try {
        handle.releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      const delta = Math.max(0, currentY - startY);
      const elapsed = Date.now() - startTime;
      const velocity = elapsed > 0 ? delta / elapsed : 0;

      // Reset inline overrides — let CSS transitions take back over
      this.containerEl.style.transform = "";
      if (this.backdropEl) this.backdropEl.style.opacity = "";
      delete this.dataset.dragging;

      // Dismiss if dragged > 30% of sheet OR fast downward flick. Choose
      // minimize over full close when promo is active and there's still tier
      // progress to track — preserves the engagement opportunity.
      if (delta > sheetHeight * 0.3 || velocity > 0.5) {
        if (
          this.shouldShowPromo &&
          this.miniBarEl &&
          this.cartVendorQty > 0 &&
          this.computeFreeItems(this.cartVendorQty) < this.promoMaxFree
        ) {
          this.minimize();
        } else {
          this.close({ openDrawer: false });
        }
      }
      // Else: implicit snap-back via class-driven CSS transition
    };

    handle.addEventListener("pointerdown", onPointerDown);
    handle.addEventListener("pointermove", onPointerMove);
    handle.addEventListener("pointerup", onPointerUp);
    handle.addEventListener("pointercancel", onPointerUp);
  }

  /* ============================================================
     Adaptive Frequency: Counter, Cooldown, Mini-Toast, Soft-Disable
     sessionStorage-keys:
       - mamm-popup-adaptive-count        → number of adds in current session
       - mamm-popup-adaptive-last         → timestamp of last add (cooldown anchor)
       - mamm-popup-adaptive-disabled     → "1" wenn User Empfehlungen ausgeblendet hat
     ============================================================ */

  evaluateAdaptiveDecision() {
    if (!this.adaptiveEnabled) return "full";

    // Cooldown-Check: bei langer Add-Pause vergessen wir den Counter,
    // damit der nächste Add wieder als frische "Mini-Session" startet.
    const lastActivity = this.getAdaptiveLastActivity();
    const now = Date.now();
    if (lastActivity && now - lastActivity > this.adaptiveCooldownMs) {
      this.resetAdaptive();
    }

    const newCount = this.getAdaptiveCount() + 1;
    this.setAdaptiveCount(newCount);
    this.setAdaptiveLastActivity(now);

    // Soft-Disable hat Vorrang: User hat explizit "Empfehlungen ausblenden"
    // gewählt → für die restliche Session immer Toast.
    if (this.isSessionDisabled()) return "toast";

    if (newCount < this.adaptiveThreshold) return "full";
    return "toast";
  }

  getAdaptiveCount() {
    try {
      const v = sessionStorage.getItem("mamm-popup-adaptive-count");
      return v ? Number(v) || 0 : 0;
    } catch {
      return 0;
    }
  }

  setAdaptiveCount(n) {
    try {
      sessionStorage.setItem("mamm-popup-adaptive-count", String(n));
    } catch {
      // ignore (private mode)
    }
  }

  getAdaptiveLastActivity() {
    try {
      const v = sessionStorage.getItem("mamm-popup-adaptive-last");
      return v ? Number(v) || 0 : 0;
    } catch {
      return 0;
    }
  }

  setAdaptiveLastActivity(ts) {
    try {
      sessionStorage.setItem("mamm-popup-adaptive-last", String(ts));
    } catch {
      // ignore
    }
  }

  isSessionDisabled() {
    try {
      return sessionStorage.getItem("mamm-popup-adaptive-disabled") === "1";
    } catch {
      return false;
    }
  }

  setSessionDisabled(disabled) {
    try {
      if (disabled) {
        sessionStorage.setItem("mamm-popup-adaptive-disabled", "1");
      } else {
        sessionStorage.removeItem("mamm-popup-adaptive-disabled");
      }
    } catch {
      // ignore
    }
  }

  /** Reset Counter only — sessionDisabled bleibt erhalten (User-Wahl respektieren). */
  resetAdaptiveCount() {
    this.setAdaptiveCount(0);
    this.setAdaptiveLastActivity(0);
  }

  /** Full Reset: Counter UND sessionDisabled zurück. Nach Cooldown oder explizitem
   *  "Empfehlungen anzeigen"-Klick im Toast. */
  resetAdaptive() {
    this.resetAdaptiveCount();
    this.setSessionDisabled(false);
  }

  /**
   * Mini-Toast anzeigen. Versucht, den Produkttitel zu ermitteln (vom PDP),
   * lädt Cart-Count und rendert "✓ X hinzugefügt · Warenkorb (N) →".
   */
  async showToast(productId) {
    if (!this.toastEl) return;
    this.cancelToastDismiss();

    // Produkt-Titel: vom PDP lesen (data-product-title oder h1)
    const title = this.detectCurrentProductTitle();
    this.lastAddedTitle = title;
    if (this.toastHeadlineEl) {
      this.toastHeadlineEl.textContent = title
        ? `${title} hinzugefügt`
        : "Zum Warenkorb hinzugefügt";
    }

    // Cart-Count im Hintergrund holen + nachträglich einsetzen
    if (this.toastCartEl) {
      this.toastCartEl.textContent = "Warenkorb öffnen";
    }
    this.fetchCartCountForToast();

    this.toastEl.hidden = false;
    // requestAnimationFrame, damit hidden=false visible ist und CSS-Transition feuert
    requestAnimationFrame(() => {
      if (this.toastEl) this.toastEl.dataset.visible = "true";
    });

    this.scheduleToastDismiss();
  }

  async fetchCartCountForToast() {
    try {
      const res = await fetch(`${this.cartUrl}.js`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return;
      const cart = await res.json();
      const count = Number(cart.item_count) || 0;
      if (this.toastCartEl) {
        this.toastCartEl.textContent =
          count > 0 ? `Warenkorb (${count}) öffnen` : "Warenkorb öffnen";
      }
    } catch {
      // best-effort — Label bleibt "Warenkorb öffnen"
    }
  }

  hideToast({ persist = true } = {}) {
    if (!this.toastEl) return;
    this.cancelToastDismiss();
    delete this.toastEl.dataset.visible;
    // Hide nach Exit-Animation
    setTimeout(() => {
      if (this.toastEl && !this.toastEl.dataset.visible) {
        this.toastEl.hidden = true;
      }
    }, 240);
    // persist=false: keine Side-Effects, nur UI-hide. Wird z.B. von
    // handleToastShowRecommendations genutzt, das selbst Counter resettet.
    if (!persist) return;
  }

  scheduleToastDismiss() {
    this.cancelToastDismiss();
    this.toastDismissTimer = setTimeout(() => {
      this.hideToast({ persist: false });
    }, this.toastDurationMs);
  }

  cancelToastDismiss() {
    if (this.toastDismissTimer) {
      clearTimeout(this.toastDismissTimer);
      this.toastDismissTimer = null;
    }
  }

  pauseToastDismiss() {
    this.cancelToastDismiss();
  }

  resumeToastDismiss() {
    if (this.toastEl && this.toastEl.dataset.visible === "true") {
      this.scheduleToastDismiss();
    }
  }

  /**
   * Klick auf Toast-Body (Headline/Cart-Label) → Cart-Drawer öffnen.
   * Counter-Reset, weil User Cart-Engagement gewählt hat → neue Mini-Session.
   */
  handleToastOpenCart() {
    this.hideToast({ persist: false });
    this.resetAdaptiveCount();
    this.openCartDrawerNow();
  }

  /**
   * Klick auf "Empfehlungen anzeigen" im Toast → volles Popup für aktuelles
   * Produkt anzeigen + Full Reset (Counter UND sessionDisabled zurück).
   */
  async handleToastShowRecommendations() {
    this.hideToast({ persist: false });
    this.resetAdaptive();

    const productId = this.detectCurrentProductId();
    if (!productId) return;

    this.shouldSuppressDrawer = true;
    this.lastTriggerProductId = productId;

    const currentVendor = this.detectCurrentProductVendor();
    this.shouldShowPromo =
      this.promoActive &&
      !!currentVendor &&
      !!this.promoVendor &&
      currentVendor.toLowerCase() === this.promoVendor.toLowerCase();

    this.dataset.showMinimize =
      this.desktopMinimize && this.shouldShowPromo ? "true" : "false";

    this.renderSkeletons(4);
    this.open();

    if (this.shouldShowPromo) {
      this.refreshCartVendorQty();
    } else if (this.promoEl) {
      this.promoEl.hidden = true;
    }

    try {
      await this.loadRecommendations(productId);
      this.render();
    } catch (err) {
      console.warn("[CartAddPopup] failed to load recommendations after toast-recovery", err);
      this.shouldSuppressDrawer = false;
    }
  }

  /**
   * Klick auf "Empfehlungen ausblenden" im vollen Popup → für den Rest der
   * Session nur noch Mini-Toasts, Popup schließen.
   */
  handleHideRecommendations() {
    this.setSessionDisabled(true);
    this.close({ openDrawer: false });
  }

  detectCurrentProductTitle() {
    const titleEl = document.querySelector("[data-product-title]");
    if (titleEl?.textContent?.trim()) return titleEl.textContent.trim();
    const h1 = document.querySelector("main h1, .product__title, .product-title");
    if (h1?.textContent?.trim()) return h1.textContent.trim();
    return "";
  }

  safeProductUrl(url, handle) {
    if (typeof url === "string") {
      if (url.startsWith("/")) return url;
      try {
        const parsed = new URL(url, location.origin);
        if (parsed.origin === location.origin) {
          return parsed.pathname + parsed.search + parsed.hash;
        }
      } catch {
        // fall through
      }
    }
    return handle ? `/products/${encodeURIComponent(handle)}` : "/";
  }

  isSafeImageUrl(url) {
    if (!url || typeof url !== "string") return false;
    return url.startsWith("https://") || url.startsWith("//") || url.startsWith("/");
  }

  /**
   * Robust image-URL extraction. Different Shopify endpoints return image data
   * in different shapes:
   *   - section render: featured_image is a string URL
   *   - /products.json + /collections/X/products.json: images is array of strings
   *     and featured_image (if present) is an object {src, ...}
   */
  extractImageUrl(p) {
    if (!p) return null;
    if (typeof p.featured_image === "string") return p.featured_image;
    if (p.featured_image && typeof p.featured_image === "object") {
      return p.featured_image.src || p.featured_image.url || null;
    }
    if (Array.isArray(p.images) && p.images.length > 0) {
      const first = p.images[0];
      if (typeof first === "string") return first;
      if (first && typeof first === "object") return first.src || first.url || null;
    }
    return null;
  }
}

if (!customElements.get("popup-cart-add")) {
  customElements.define("popup-cart-add", PopupCartAdd);
}
